import { FC } from 'react';
import { observer } from 'mobx-react-lite';

import BellIcon from 'assets/icons/bell.svg';
import { Modal } from 'components/Modal/Modal';
import { store } from 'store';
import { NewsStory } from 'store/galaxy/types';

import * as S from './BlockchainNews.styles';

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const StoryCard: FC<{ story: NewsStory }> = ({ story }) => (
  <S.StoryCard>
    <S.StoryTitle>{story.title}</S.StoryTitle>
    <S.Tags>
      {story.tags.map((tag) => (
        <S.Tag key={tag}>{tag}</S.Tag>
      ))}
    </S.Tags>
    <S.EpisodeList>
      {[...story.episodes]
        .sort((a, b) => b.episode_number - a.episode_number)
        .map((ep) => (
          <S.Episode key={ep.episode_number}>
            <S.EpisodeSummary>{ep.summary}</S.EpisodeSummary>
            <S.EpisodeDate>{formatTimeAgo(ep.created_at)}</S.EpisodeDate>
          </S.Episode>
        ))}
    </S.EpisodeList>
  </S.StoryCard>
);

export const BlockchainNews: FC = observer(() => {
  const {
    newsStories,
    readNewsStories,
    totalNewEpisodes,
    isNewsModalOpen,
    openNewsModal,
    closeNewsModal,
    fetchNotifications,
  } = store.galaxyStore;

  const hasNew = totalNewEpisodes > 0;

  return (
    <>
      <S.NewsButton $active={hasNew} onClick={openNewsModal}>
        {hasNew && (
          <S.BellWrapper>
            <BellIcon />
            <S.Badge>new</S.Badge>
          </S.BellWrapper>
        )}
        Blockchain news
      </S.NewsButton>

      <Modal
        isOpen={isNewsModalOpen}
        onClose={closeNewsModal}
        title="Blockchain News"
        maxWidth={800}
      >
        <S.ModalContent>
          <S.ModalHeader>
            <S.ModalTitle>Blockchain News</S.ModalTitle>
            {newsStories.length === 0 && (
              <S.RefreshButton onClick={fetchNotifications}>Refresh</S.RefreshButton>
            )}
          </S.ModalHeader>

          {newsStories.length === 0 && readNewsStories.length === 0 && (
            <S.EmptyState>No news yet</S.EmptyState>
          )}

          {newsStories.map((story) => (
            <StoryCard key={story.story_id} story={story} />
          ))}

          {readNewsStories.length > 0 && (
            <>
              <S.ReadDivider>Read messages</S.ReadDivider>
              {readNewsStories.map((story) => (
                <StoryCard key={story.story_id} story={story} />
              ))}
            </>
          )}
        </S.ModalContent>
      </Modal>
    </>
  );
});
